// Configuración → Procesos: la TIRA DEL AGENTE (F4-2).
//
// La pantalla contaba un solo sistema en una sola pila: lo que es del AGENTE
// (su saludo, su consentimiento, lo que ya sabe atender) quedaba mezclado con lo
// que es de UN PROCESO (sus etapas, sus preguntas). Aquí vive la mitad de
// arriba: los hechos del agente, que no cambian con el proceso que se elija
// abajo.
//
// Lee la MISMA consulta que la tarjeta de la vista previa
// (`['agent-compiled-preview', botId]`), así que las dos mitades salen de una
// sola lectura y nunca se contradicen.
//
// CASI sólo lectura: la única cosa que esta tira ESCRIBE es el saludo (W4'-4a).
// Dejó de ser un enlace al editor de flujos porque ahí ya no se puede escribir
// (el compilador es dueño del flujo principal y un flujo administrado refusa las
// ediciones a mano), así que aquel enlace era una puerta pintada. El saludo se
// guarda en su columna y el agente lo dice en la siguiente publicación.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { getAgentPreview } from './agent-preview-data.js'
import { GENERATE_NO_AGENT } from './pipelines-copy.js'
import { Alert, Button, Chip, FormField, Modal, Textarea } from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { saveWorkerEditable } from '../workers/workers-data.js'

/** Qué es esta tira: lo del agente, antes de bajar a un proceso. */
const STRIP_TITLE = 'Tu agente'
const STRIP_INTRO =
  'Lo que tu agente trae puesto hoy. No cambia con el proceso que elijas más abajo.'
/** Con más de una identidad, la vista previa es de UNA de ellas y hay que
 *  decirlo: el menú cuelga de la identidad de canal, no del agente (D15). */
function previewIdentityLine(channel: string, displayName: string, count: number): string {
  return `Vista previa para ${channel} ${displayName}; este agente atiende ${count} canales.`
}
/** Con más de un agente, la tira dice CUÁL se está viendo y que los demás
 *  existen (F4-2). Sin esto, un espacio con dos agentes leía la tira como si
 *  describiera a todos. */
function multiAgentLine(name: string, count: number): string {
  return `Estás viendo el agente "${name}". Tienes ${count} agentes; los demás también atienden sus canales.`
}
/** El saludo con el que el agente abre. Es lo PRIMERO que el prospecto lee, así
 *  que la tira lo muestra: un agente cuya primera frase no se ve no se puede
 *  juzgar. Y cuando no hay ninguno se dice, en vez de dejar el renglón en blanco. */
const GREETING_TITLE = 'Saludo'
const GREETING_EMPTY = 'Sin saludo configurado todavía.'
/** El saludo se edita AQUÍ (W4'-4a). Antes era un enlace al editor de flujos, y
 *  desde que el compilador es dueño del flujo principal ese editor refusa
 *  escribir: el enlace llevaba a una negativa. Se guarda en la configuración del
 *  agente, que es de donde el compilador emite el nodo de arranque. */
const GREETING_EDIT = 'Editar'
const GREETING_DIALOG_TITLE = 'Editar el saludo'
const GREETING_FIELD_LABEL = 'Con qué abre el agente'
const GREETING_FIELD_HELPER = 'Es la primera frase que lee quien te escribe.'
/** CUÁNDO llega al agente. La vista previa cambia en cuanto se guarda, y la
 *  conversación cambia al publicar: decirlo aquí es lo que evita que el cliente
 *  crea que ya está en vivo. */
const GREETING_PUBLISH_HINT = 'El saludo se publica con el botón Publicar.'
const GREETING_SAVE = 'Guardar'
const GREETING_CANCEL = 'Cancelar'
const GREETING_SAVE_ERROR = 'No pudimos guardar el saludo.'
/** D6: el consentimiento no es una etapa del flujo y no se edita desde aquí. Se
 *  dibuja igual, porque el prospecto lo vive primero. */
const CONSENT_HINT =
  'Siempre va primero y no se edita desde aquí: la pide el aviso de privacidad, no el flujo.'
/** Las palabras del modo son LAS MISMAS de Configuración → Privacidad
 *  (`ConsentSettings`): dos formas de nombrar una opción son dos opciones para
 *  quien lee. */
const CONSENT_MODE_LABEL: Record<'implicit' | 'affirmative', string> = {
  implicit: 'Al continuar la conversación',
  affirmative: 'Respondiendo "acepto"',
}
const CONSENT_OWN_NOTICE = 'Aviso propio'
const CONSENT_DEFAULT_NOTICE = 'Aviso estándar de la plataforma'
const CONSENT_LINK = 'Configurar aviso'
const CONSENT_TAB_HREF = '/configuracion?tab=privacidad'
/** Las opciones BASE del menú: lo que el agente atiende sin que salga de un
 *  proceso. Son del AGENTE, así que viven aquí y no bajo un proceso. */
const CATEGORIES_TITLE = 'Lo que el agente ya sabe atender'
/** De dónde sale cada opción base, en palabras del cliente. */
const CATEGORY_SOURCE_LABEL: Record<string, string> = {
  agenda: 'Agenda',
  catalogo: 'Catálogo',
  conocimiento: 'Conocimiento',
  urgencia: 'Urgencias',
  persona: 'Hablar con una persona',
  despedida: 'Despedida',
  otra: 'Otra opción',
}
/** Ningún espacio tiene conexiones configuradas todavía (D15), así que la vista
 *  previa asume todos los procesos en su orden. Se dice: una suposición
 *  presentada como un hecho configurado es una mentira pequeña que se descubre
 *  tarde. */
const SYNTHESIZED =
  'Se están tomando todos tus procesos, en el orden en que los tienes. Todavía no eliges ' +
  'cuáles atiende cada canal.'

export interface AgentStripProps {
  /** El agente que se está describiendo. */
  botId: string
  /** Su nombre, para poder decir cuál es cuando hay más de uno. */
  agentName: string
  /** Cuántos agentes tiene el espacio: con ninguno la tira dice por qué no hay
   *  nada que mostrar, en vez de quedarse en blanco; con varios dice cuál es
   *  éste. */
  agentCount: number
}

export function AgentStrip({ botId, agentName, agentCount }: AgentStripProps): JSX.Element {
  const agentPreview = useQuery({
    queryKey: ['agent-compiled-preview', botId],
    queryFn: () => getAgentPreview(botId),
    enabled: botId !== '',
    staleTime: 60_000,
  })
  const qc = useQueryClient()
  // EL DIÁLOGO DEL SALUDO. Se abre con lo que el agente dice hoy, que es lo
  // mismo que la tira enseña: la vista previa es la única lectura, y editar
  // sobre otra cosa sería escribir encima de un valor que nadie vio.
  const [greetingOpen, setGreetingOpen] = useState(false)
  const [greetingDraft, setGreetingDraft] = useState('')
  const [greetingError, setGreetingError] = useState('')

  const data = agentPreview.data

  const saveGreeting = useMutation({
    mutationFn: (greeting: string) => saveWorkerEditable(botId, { greeting }),
    onSuccess: async () => {
      // La vista previa compilada, la del publicar y los flujos del agente
      // describen todos al mismo agente: quedarse con una vieja es la pantalla
      // contando dos saludos a la vez.
      await qc.invalidateQueries({ queryKey: ['agent-compiled-preview', botId] })
      await qc.invalidateQueries({ queryKey: ['agent-publish-preview', botId] })
      await qc.invalidateQueries({ queryKey: ['worker-flows', botId] })
      setGreetingOpen(false)
    },
    onError: (err: unknown) => setGreetingError(apiErrorMessage(err, GREETING_SAVE_ERROR)),
  })

  function openGreeting(): void {
    setGreetingDraft(data?.preview.greeting ?? '')
    setGreetingError('')
    setGreetingOpen(true)
  }

  function closeGreeting(): void {
    if (saveGreeting.isPending) return
    setGreetingOpen(false)
    setGreetingError('')
  }

  return (
    <div className="rounded-xl border border-line bg-surface p-5" data-testid="agent-strip">
      <h2 className="text-[13px] font-semibold text-text">{STRIP_TITLE}</h2>
      <p className="mt-1.5 text-[11.5px] text-text-3">{STRIP_INTRO}</p>
      {agentCount > 1 && (
        <p className="mt-1 text-[11.5px] text-text-3" data-testid="agent-strip-multi">
          {multiAgentLine(agentName, agentCount)}
        </p>
      )}
      {data && data.preview.channelIdentityCount > 1 && data.preview.channelIdentity && (
        <p className="mt-1 text-[11.5px] text-text-3" data-testid="agent-preview-identity">
          {previewIdentityLine(
            data.preview.channelIdentity.channelLabel,
            data.preview.channelIdentity.displayName,
            data.preview.channelIdentityCount,
          )}
        </p>
      )}

      {agentCount === 0 && <p className="mt-3 text-[12px] text-text-2">{GENERATE_NO_AGENT}</p>}
      {agentPreview.isError && (
        <div className="mt-3">
          <Alert tone="warn">
            {apiErrorMessage(agentPreview.error, 'No se pudo cargar la vista previa.')}
          </Alert>
        </div>
      )}

      {data && (
        <div className="mt-4 flex flex-col gap-4">
          <div data-testid="agent-preview-greeting">
            <p className="text-[11.5px] font-medium text-text-3">{GREETING_TITLE}</p>
            {data.preview.greeting === '' ? (
              <p className="mt-0.5 text-[12.5px] italic text-text-3">{GREETING_EMPTY}</p>
            ) : (
              <p className="mt-0.5 text-[12.5px] text-text">{data.preview.greeting}</p>
            )}
            {/* La tira dice "trae puesto hoy" y el saludo sale de la
                configuración, que puede ir adelante de lo publicado: el renglón
                cierra esa brecha sin rastrear estados. Siempre es verdad. */}
            <p className="mt-0.5 text-[11.5px] text-text-3" data-testid="agent-greeting-hint">
              {GREETING_PUBLISH_HINT}
            </p>
            {/* Variante link, no ghost: el control hermano de abajo (Configurar
                aviso) es un enlace azul, y un "Editar" gris al lado se lee como
                un pie de texto, no como una acción. Mismo peso para dos puertas
                vecinas del mismo rango. */}
            <Button
              variant="link"
              size="sm"
              className="mt-1"
              data-testid="agent-greeting-edit"
              onClick={openGreeting}
            >
              {GREETING_EDIT}
            </Button>
          </div>

          {/* El consentimiento PRIMERO y fijo (D6). No es una etapa del flujo: es
              la puerta del pipeline, y por eso se dibuja aparte y sin acciones. */}
          {data.preview.consentStage && (
            <div
              className="rounded-lg border border-line bg-surface-2 p-3"
              data-testid="agent-preview-consent"
            >
              <div className="flex items-center gap-2">
                <span className="text-[12.5px] font-medium text-text">
                  {data.preview.consentStage.name}
                </span>
                <Chip tone="draft">Fijo</Chip>
              </div>
              <p className="mt-1 text-[11.5px] text-text-3">{CONSENT_HINT}</p>
              {/* Lo configurable, en palabras: el modo con el que la persona
                  acepta, la versión de los términos que se le pide, y de quién es
                  el aviso que recibe. */}
              <div className="mt-2 flex flex-col gap-0.5 text-[11.5px] text-text-2">
                <p data-testid="agent-preview-consent-mode">
                  Modo: {CONSENT_MODE_LABEL[data.preview.consentStage.acceptanceMode]}
                </p>
                <p data-testid="agent-preview-consent-version">
                  Versión de términos: {data.preview.consentStage.termsVersion}
                </p>
                <p data-testid="agent-preview-consent-notice">
                  {data.preview.consentStage.usingOwnNotice
                    ? CONSENT_OWN_NOTICE
                    : CONSENT_DEFAULT_NOTICE}
                </p>
              </div>
              {data.preview.consentStage.noticeExcerpt !== '' && (
                <p
                  className="mt-1.5 text-[11.5px] italic text-text-3"
                  data-testid="agent-preview-consent-excerpt"
                >
                  {data.preview.consentStage.noticeExcerpt}
                </p>
              )}
              <Link
                to={CONSENT_TAB_HREF}
                className="mt-2 inline-block text-[11.5px] font-semibold text-link underline"
              >
                {CONSENT_LINK}
              </Link>
            </div>
          )}

          {/* Las opciones BASE: el fondo del menú, que no sale de ningún proceso.
              Vacío = no se dibuja nada, en vez de un encabezado sin lista. */}
          {data.preview.categories.length > 0 && (
            <div>
              <h3 className="text-[12px] font-semibold text-text">{CATEGORIES_TITLE}</h3>
              <ul className="mt-2 flex flex-col gap-1">
                {data.preview.categories.map((c) => (
                  <li
                    key={c.key}
                    className="text-[12px] text-text-2"
                    data-testid="agent-preview-category"
                  >
                    {c.label}
                    <span className="text-text-3">
                      {' · '}
                      {CATEGORY_SOURCE_LABEL[c.source] ?? c.source}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.synthesizedConnections && (
            <p className="text-[11.5px] text-text-3" data-testid="agent-preview-synthesized">
              {SYNTHESIZED}
            </p>
          )}
        </div>
      )}

      {/* La ÚNICA escritura de esta tira. Un campo, porque es un solo hecho. */}
      <Modal
        open={greetingOpen}
        onClose={closeGreeting}
        title={GREETING_DIALOG_TITLE}
        busy={saveGreeting.isPending}
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              disabled={saveGreeting.isPending}
              onClick={closeGreeting}
            >
              {GREETING_CANCEL}
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              data-testid="agent-greeting-save"
              loading={saveGreeting.isPending}
              disabled={saveGreeting.isPending || greetingDraft.trim() === ''}
              onClick={() => saveGreeting.mutate(greetingDraft.trim())}
            >
              {GREETING_SAVE}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <FormField
            label={GREETING_FIELD_LABEL}
            htmlFor="agent-greeting"
            helper={GREETING_FIELD_HELPER}
          >
            <Textarea
              id="agent-greeting"
              value={greetingDraft}
              maxLength={500}
              onChange={(e) => setGreetingDraft(e.target.value)}
            />
          </FormField>
          <p className="text-[11.5px] text-text-3">{GREETING_PUBLISH_HINT}</p>
          {greetingError !== '' && <Alert tone="warn">{greetingError}</Alert>}
        </div>
      </Modal>
    </div>
  )
}
